import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import {
  TMAI_MEMBERSHIP_FRAMEWORK_2022_03,
  buildPublishablePacket,
  type MembershipReadinessReport,
} from '@dosfilos/domain';
import { useUiStore } from '../../../stores/ui.store';
import { formatDate } from '../../../lib/i18n-format';

// ─── Lo que va a cruzar, antes de que cruce (R1-PR5) ────────────────────────
//
// EL ANTÍDOTO NO ES PRACTICAR EL TRÁMITE, ES VER EL RESULTADO. Se descartó un
// "modo pruebas" porque el sandbox ya existe —todo lo anterior al envío es
// privado y reversible— y lo único irreversible es el envío. Lo que faltaba no
// era ensayar: era saber qué se manda.
//
// SE CONSTRUYE CON `buildPublishablePacket`, LA MISMA FUNCIÓN QUE USA EL
// SERVIDOR. No es una maqueta de la UI que "se parece" al paquete: es el paquete.
// Si mañana `PACKET_FIELD_POLICY` deja de publicar un campo, esta pantalla deja
// de mostrarlo el mismo día, sin que nadie se acuerde de venir a actualizarla.
// Una vista previa que se construye aparte es una promesa que se rompe sola.
//
// Y SIRVE COMO PRUEBA DE PRIVACIDAD: el TC ve con sus ojos que lo que viaja son
// conteos y estados, sin un solo nombre de persona.

const CRITERION = new Map(TMAI_MEMBERSHIP_FRAMEWORK_2022_03.criteria.map((c) => [c.id, c]));

const ICON: Record<string, typeof CheckCircleIcon> = {
  met: CheckCircleIcon,
  not_met: ErrorOutlineIcon,
  no_data: HelpOutlineIcon,
  not_applicable: RemoveCircleOutlineIcon,
  partial: HelpOutlineIcon,
};

/** `no_data` NUNCA en rojo: ausente no es incumplido, y el TC no debería leer
 *  una alarma donde solo falta un dato de origen. */
const COLOR: Record<string, string> = {
  met: 'success.main',
  not_met: 'error.main',
  no_data: 'text.disabled',
  not_applicable: 'text.disabled',
  partial: 'warning.main',
};

export function PacketPreviewDialog({
  report,
  onClose,
}: {
  report: MembershipReadinessReport;
  onClose: () => void;
}) {
  const { t } = useTranslation('membership');
  const lang = (useUiStore((s) => s.language) ?? 'es') as 'es' | 'en';
  const packet = buildPublishablePacket(report);

  const byCat = new Map<string, typeof packet.criteria>();
  for (const c of packet.criteria) {
    const list = byCat.get(c.category) ?? [];
    list.push(c);
    byCat.set(c.category, list);
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('network.previewTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('network.previewSubtitle', {
            date: formatDate(packet.generatedAt, {
              dateStyle: 'long',
            }),
          })}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* La garantía de privacidad, dicha y verificable en la misma pantalla. */}
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          {t('network.previewNoPii')}
        </Alert>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={t(`stages.${packet.stage}`)}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            label={t(`network.overall.${packet.overallStatus}`)}
            color={packet.overallStatus === 'ready' ? 'success' : 'warning'}
          />
        </Stack>

        {[...byCat.entries()].map(([category, items]) => (
          <Paper variant="outlined" key={category} sx={{ mb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}
            >
              <Typography sx={{ fontWeight: 700 }}>{t(`categories.${category}`)}</Typography>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {items.filter((c) => c.status === 'met').length}/{items.length}
              </Typography>
            </Stack>
            <Divider />
            <Stack divider={<Divider />}>
              {items.map((c) => {
                const Icon = ICON[c.status] ?? HelpOutlineIcon;
                const def = CRITERION.get(c.criterionId);
                return (
                  <Stack
                    key={c.criterionId}
                    direction="row"
                    spacing={1.5}
                    sx={{ px: 2, py: 1.25 }}
                    alignItems="flex-start"
                  >
                    <Icon sx={{ fontSize: 19, mt: 0.25, color: COLOR[c.status] }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {def ? def.label[lang] : c.criterionId}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLOR[c.status] }}>
                        {t(`criteria.outcome.${c.status}`, { defaultValue: c.status })}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('network.previewClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
